import { Link } from 'react-router-dom';

export default function BenefitsSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-6">
            <span className="text-teal-700 text-sm font-semibold">Key Benefits</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Benefits of Selling Your Inherited Home to Us
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            When you choose T-Rex Real Estate to buy your inherited property, you gain access to advantages that traditional home sales simply can't offer.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/71bda3d565562e2bc0c9aa76d52649a4.jpg"
                alt="Professional Inherited Home Services"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-8">
              Immediate Financial Relief
            </h3>
            
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Stop Paying Property Expenses</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Once you inherit a property, you become responsible for property taxes, insurance, utilities, and maintenance costs. These expenses add up quickly, especially if the home sits empty. By selling to us, you immediately stop these ongoing costs and convert the property into cash.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Avoid Liability Issues</h4>
                    <p className="text-gray-600 leading-relaxed">
                      As the owner of an inherited property, you're liable for anything that happens on the premises. From slip-and-fall accidents to vandalism or code violations, these risks can result in costly lawsuits. Selling quickly eliminates this liability and protects your financial future.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Access Your Inheritance Faster</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Traditional home sales in El Paso take an average of 3-6 months from listing to closing. During this time, your inheritance remains tied up in the property. We can close in as little as 7 days, giving you immediate access to your inheritance when you need it most. <Link to="/title-issues" className="text-teal-600 hover:text-teal-700 font-semibold underline cursor-pointer">Learn more about title issues</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">
              Emotional and Practical Peace of Mind
            </h3>
            
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Reduce Emotional Stress</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Dealing with an inherited property while grieving is emotionally exhausting. Every visit to the home, every decision about belongings, and every repair issue brings back painful memories. Our compassionate team handles the details so you can focus on healing and remembering your loved one positively. <Link to="/" className="text-teal-600 hover:text-teal-700 font-semibold underline cursor-pointer">Visit our homepage</Link> to see how we can help.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-group-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Resolve Family Conflicts</h4>
                    <p className="text-gray-600 leading-relaxed">
                      When multiple heirs inherit a property, disagreements about what to do with it can damage family relationships. A quick cash sale provides a clean, fair solution that satisfies everyone. We can even handle situations where heirs want to be bought out individually.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">No Need to Travel to El Paso</h4>
                    <p className="text-gray-600 leading-relaxed">
                      If you live out of state, managing an inherited El Paso property from afar is nearly impossible. We handle everything locally, including property access, inspections, and closing. You can complete the entire sale process remotely without making a single trip to Texas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=relieved%20person%20signing%20inherited%20property%20sale%20documents%20peaceful%20expression%20weight%20lifted%20modern%20office%20setting%20professional%20photography%20warm%20natural%20lighting%20clean%20simple%20background&width=800&height=900&seq=inherited-benefits-002&orientation=portrait"
                alt="Peace of Mind Selling Inherited Home"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}